import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Grid,
  Switch,
  Slider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionIcon from '@mui/icons-material/Description';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import FrameIoFolderPicker from '../../ui-components/FrameIoFolderPicker';
import FontPicker from './FontPicker';
import EuProjectVeld from '../../ui-components/EuProjectVeld';

const accordionSx = {
  mb: 2,
  borderRadius: '8px !important',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0, mb: 2 },
};

/**
 * Step 1 – Instellingen & tekst.
 */
function StepSettings({
  // Basis
  language, setLanguage,
  aspectRatio, setAspectRatio,
  videoInfo,
  // Client presets
  clientPresets,
  selectedClientPreset, setSelectedClientPreset,
  activePreset,
  defaultPreset,
  setPresetManagerOpen,
  // Script
  getScriptRootProps, getScriptInputProps,
  scriptFile, setScriptFile,
  scriptText, setScriptText,
  skipCorrection, setSkipCorrection,
  // EU-verwerking: optioneel klantproject
  projectNummer, setProjectNummer,
  // Subtitle style
  overridePreset, setOverridePreset,
  subtitleStyle, setSubtitleStyle,
  subtitleSize, setSubtitleSize,
  subtitleFont, setSubtitleFont,
  ellipsisStyle, setEllipsisStyle,
  // Font picker deps
  allFontOptions,
  handleFontDelete,
  fontInputRef,
  handleFontUpload,
  // Social Subs / Karaoke
  isKaraoke, setIsKaraoke,
  karaokeStyle, setKaraokeStyle,
  setKaraokePresetManagerOpen,
  // Vocabulary
  vocabularyTerms,
  newVocabTerm, setNewVocabTerm,
  newVocabReplacement, setNewVocabReplacement,
  newVocabCategory, setNewVocabCategory,
  addVocabularyTerm,
  deleteVocabularyTerm,
  // Output
  sendEmail, setSendEmail,
  uploadToFrameIo, setUploadToFrameIo,
  email, setEmail,
  selectedFolderId, setSelectedFolderId,
  selectedFolderPath, setSelectedFolderPath,
  frameIoLink,
  activeAccount,
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>Instellingen &amp; tekst</Typography>

      {/* ── Sectie A: Basis ── */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Basis</Typography>

        {setProjectNummer && (
          <Box sx={{ mb: 2 }}>
            <EuProjectVeld waarde={projectNummer} onChange={setProjectNummer} />
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Taal</InputLabel>
              <Select value={language} label="Taal" onChange={(e) => setLanguage(e.target.value)}>
                <MenuItem value="auto">Automatisch (Whisper AI)</MenuItem>
                <MenuItem value="nl">Nederlands</MenuItem>
                <MenuItem value="en">Engels</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Video formaat</InputLabel>
              <Select value={aspectRatio} label="Video formaat" onChange={(e) => setAspectRatio(e.target.value)}>
                <MenuItem value="16:9">16:9 Landscape (1920x1080)</MenuItem>
                <MenuItem value="9:16">9:16 Portrait / Reels (1080x1920)</MenuItem>
                <MenuItem value="1:1">1:1 Vierkant (1080x1080)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Video info chips */}
        {videoInfo && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              {videoInfo.detectionMethod === 'resolution' ? 'Gedetecteerd:' : 'Info:'}
            </Typography>
            {videoInfo.resolution && (
              <Chip
                icon={<AspectRatioIcon />}
                label={`${videoInfo.resolution.width}x${videoInfo.resolution.height}`}
                size="small"
                variant="outlined"
              />
            )}
            {videoInfo.duration && (
              <Chip
                label={`${Math.floor(videoInfo.duration / 60)}:${String(Math.floor(videoInfo.duration % 60)).padStart(2, '0')} min`}
                size="small"
                variant="outlined"
              />
            )}
            {videoInfo.fileSize && (
              <Chip
                label={`${(videoInfo.fileSize / (1024 * 1024)).toFixed(1)} MB`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Client preset selector */}
        {clientPresets.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Klantpreset (optioneel)</InputLabel>
              <Select
                value={selectedClientPreset}
                label="Klantpreset (optioneel)"
                onChange={(e) => setSelectedClientPreset(e.target.value)}
              >
                <MenuItem value="">Geen preset</MenuItem>
                {[...new Set(clientPresets.map((p) => p.client_name))].map((client) => (
                  <MenuItem key={client} value={client}>{client}</MenuItem>
                ))}
              </Select>
              {selectedClientPreset && activePreset && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5 }}>
                  {activePreset.font_name} {activePreset.font_style}, {activePreset.font_size}px
                </Typography>
              )}
              {selectedClientPreset && !activePreset && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                  Geen preset voor {selectedClientPreset} in {aspectRatio}
                </Typography>
              )}
            </FormControl>
            <Button
              size="small"
              variant="text"
              onClick={() => setPresetManagerOpen(true)}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', mt: 0.5 }}
            >
              Beheren
            </Button>
          </Box>
        )}
      </Paper>

      {/* ── Sectie B: Ondertiteltekst (optioneel) ── */}
      <Accordion variant="outlined" sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" fontWeight={600}>Ondertiteltekst (optioneel)</Typography>
            {scriptText && (
              <Chip label={`${scriptText.length} tekens`} size="small" color="success" variant="outlined" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload het script of de voice-over tekst als referentie voor de ondertitels.
            De AI gebruikt dit als bron en blijft bij de aangeleverde tekst.
          </Typography>

          <Paper
            {...getScriptRootProps()}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: scriptFile ? '#e8f5e9' : '#fafafa',
              border: '2px dashed',
              borderColor: scriptFile ? '#4caf50' : '#ccc',
              borderRadius: 2,
              mb: 2,
              '&:hover': { borderColor: '#2196f3', backgroundColor: '#f5f5f5' },
            }}
          >
            <input {...getScriptInputProps()} />
            {scriptFile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <DescriptionIcon color="success" />
                <Typography variant="body1">{scriptFile.name}</Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setScriptFile(null);
                    setScriptText('');
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 36, color: 'text.secondary', mb: 0.5 }} />
                <Typography variant="body2" color="textSecondary">
                  Sleep je script hier of klik om te uploaden (TXT, DOCX)
                </Typography>
              </Box>
            )}
          </Paper>

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Of plak de tekst handmatig
          </Typography>
          <TextField
            fullWidth multiline rows={4} size="small"
            placeholder="Plak hier het script of de voice-over tekst..."
            value={scriptText}
            onChange={(e) => setScriptText(e.target.value)}
          />

          {scriptText && (
            <>
              <Alert severity="success" sx={{ mt: 1 }} icon={false}>
                Tekst geladen ({scriptText.length} karakters). Script wordt letterlijk als ondertiteling overgenomen.
              </Alert>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={!skipCorrection}
                    onChange={(e) => setSkipCorrection(!e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    AI-suggesties (spelling/grammatica). Standaard uit bij script.
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── Sectie C: Ondertiteling stijl (burned-in) ── */}
      <Accordion defaultExpanded variant="outlined" sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SubtitlesIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" fontWeight={600}>Ondertiteling stijl</Typography>
            {activePreset && !overridePreset && (
              <Chip label="Klantpreset actief" size="small" color="success" variant="outlined" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {activePreset && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {activePreset.font_name} {activePreset.font_style}, {activePreset.font_size}px, baseline {activePreset.baseline}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={overridePreset}
                    onChange={(e) => setOverridePreset(e.target.checked)}
                  />
                }
                label={<Typography variant="caption">Overschrijven</Typography>}
                sx={{ ml: 'auto' }}
              />
            </Box>
          )}
          {!activePreset && defaultPreset && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Standaard: {defaultPreset.font_name} {defaultPreset.font_style}, {defaultPreset.font_size}px
            </Typography>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!!activePreset && !overridePreset}>
                <InputLabel>Stijl (burned-in)</InputLabel>
                <Select value={subtitleStyle} label="Stijl (burned-in)" onChange={(e) => setSubtitleStyle(e.target.value)}>
                  <MenuItem value="box">Box - Zwarte achtergrond</MenuItem>
                  <MenuItem value="outline">Outline - Zwarte omlijning</MenuItem>
                  <MenuItem value="shadow">Shadow - Tekst met schaduw</MenuItem>
                  <MenuItem value="yellow">Geel - Klassieke stijl</MenuItem>
                  <MenuItem value="netflix">Netflix - Modern wit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!!activePreset && !overridePreset}>
                <InputLabel>Grootte</InputLabel>
                <Select value={subtitleSize} label="Grootte" onChange={(e) => setSubtitleSize(e.target.value)}>
                  <MenuItem value="small">Klein</MenuItem>
                  <MenuItem value="medium">Normaal</MenuItem>
                  <MenuItem value="large">Groot</MenuItem>
                  <MenuItem value="xlarge">Extra Groot</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FontPicker
                value={subtitleFont}
                onChange={setSubtitleFont}
                disabled={!!activePreset && !overridePreset}
                allFontOptions={allFontOptions}
                handleFontDelete={handleFontDelete}
                fontInputRef={fontInputRef}
              />
              <input
                ref={fontInputRef}
                type="file"
                accept=".ttf,.otf"
                hidden
                onChange={handleFontUpload}
              />
            </Grid>
          </Grid>

          <FormControl fullWidth size="small">
            <InputLabel>Puntjes bij zinsafbreking</InputLabel>
            <Select value={ellipsisStyle} label="Puntjes bij zinsafbreking" onChange={(e) => setEllipsisStyle(e.target.value)}>
              <MenuItem value="none">Geen puntjes (Social Media)</MenuItem>
              <MenuItem value="end_only">Alleen einde (NPO/Omroep)</MenuItem>
              <MenuItem value="both">Begin en einde (Netflix/RTL)</MenuItem>
            </Select>
          </FormControl>
        </AccordionDetails>
      </Accordion>

      {/* ── Sectie D: Social Subs ── */}
      <Accordion
        variant="outlined"
        sx={{
          ...accordionSx,
          border: isKaraoke ? '2px solid #f57c00' : undefined,
          backgroundColor: isKaraoke ? '#fff8e1' : undefined,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            <MusicNoteIcon sx={{ fontSize: 20, color: isKaraoke ? '#f57c00' : 'text.secondary' }} />
            <Typography variant="subtitle2" fontWeight={600}>Social Subs</Typography>
            {isKaraoke && <Chip label="Aan" size="small" color="warning" />}
          </Box>
          <Switch
            checked={isKaraoke}
            onChange={(e) => { e.stopPropagation(); setIsKaraoke(e.target.checked); }}
            onClick={(e) => e.stopPropagation()}
            color="warning"
            size="small"
          />
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {/* Preset button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button size="small" variant="outlined" onClick={() => setKaraokePresetManagerOpen(true)}>
              Presets
            </Button>
          </Box>

          {/* Social Subs styling panel */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>Lettertype</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FontPicker
                value={karaokeStyle.fontName}
                onChange={(v) => setKaraokeStyle((s) => ({ ...s, fontName: v || 'Arial' }))}
                allFontOptions={allFontOptions}
                handleFontDelete={handleFontDelete}
                fontInputRef={fontInputRef}
              />
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <Typography variant="caption" color="text.secondary">Grootte: {karaokeStyle.fontSize}px</Typography>
                <Slider
                  value={karaokeStyle.fontSize}
                  onChange={(_, v) => setKaraokeStyle((s) => ({ ...s, fontSize: v }))}
                  min={24} max={72} step={2} size="small"
                />
              </Box>
              <FormControlLabel
                control={<Checkbox checked={karaokeStyle.bold} onChange={(e) => setKaraokeStyle((s) => ({ ...s, bold: e.target.checked }))} size="small" />}
                label="Bold"
              />
              <FormControlLabel
                control={<Checkbox checked={karaokeStyle.uppercase} onChange={(e) => setKaraokeStyle((s) => ({ ...s, uppercase: e.target.checked }))} size="small" />}
                label="HOOFDLETTERS"
              />
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>Kleuren</Typography>
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Tekst', colorKey: 'textColor', opacityKey: 'textOpacity' },
                { label: 'Highlight', colorKey: 'highlightColor', opacityKey: 'highlightOpacity' },
                { label: 'Achtergrond', colorKey: 'bgColor', opacityKey: 'bgOpacity' },
              ].map(({ label, colorKey, opacityKey }) => (
                <Box key={colorKey} sx={{ minWidth: 140 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <input
                      type="color"
                      value={karaokeStyle[colorKey]}
                      onChange={(e) => setKaraokeStyle((s) => ({ ...s, [colorKey]: e.target.value }))}
                      style={{ width: 36, height: 28, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">{karaokeStyle[opacityKey]}%</Typography>
                      <Slider
                        value={karaokeStyle[opacityKey]}
                        onChange={(_, v) => setKaraokeStyle((s) => ({ ...s, [opacityKey]: v }))}
                        min={0} max={100} size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>Outline &amp; Schaduw</Typography>
              <FormControlLabel
                control={<Checkbox checked={karaokeStyle.outlineEnabled} onChange={(e) => setKaraokeStyle((s) => ({ ...s, outlineEnabled: e.target.checked }))} size="small" />}
                label="Outline aan"
                sx={{ ml: 1 }}
              />
              <FormControlLabel
                control={<Checkbox checked={karaokeStyle.shadowEnabled} onChange={(e) => setKaraokeStyle((s) => ({ ...s, shadowEnabled: e.target.checked }))} size="small" />}
                label="Schaduw aan"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              {[
                ...(karaokeStyle.outlineEnabled ? [{ label: 'Outline', colorKey: 'outlineColor', opacityKey: 'outlineOpacity', sizeKey: 'outlineWidth', sizeLabel: 'Dikte' }] : []),
                ...(karaokeStyle.shadowEnabled ? [{ label: 'Schaduw', colorKey: 'shadowColor', opacityKey: 'shadowOpacity', sizeKey: 'shadowDepth', sizeLabel: 'Diepte' }] : []),
              ].map(({ label, colorKey, opacityKey, sizeKey, sizeLabel }) => (
                <Box key={colorKey} sx={{ minWidth: 180 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="color"
                      value={karaokeStyle[colorKey]}
                      onChange={(e) => setKaraokeStyle((s) => ({ ...s, [colorKey]: e.target.value }))}
                      style={{ width: 36, height: 28, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Opacity: {karaokeStyle[opacityKey]}%</Typography>
                      <Slider
                        value={karaokeStyle[opacityKey]}
                        onChange={(_, v) => setKaraokeStyle((s) => ({ ...s, [opacityKey]: v }))}
                        min={0} max={100} size="small"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">{sizeLabel}: {karaokeStyle[sizeKey]}</Typography>
                      <Slider
                        value={karaokeStyle[sizeKey]}
                        onChange={(_, v) => setKaraokeStyle((s) => ({ ...s, [sizeKey]: v }))}
                        min={0} max={4} step={0.5} size="small"
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>Animatie &amp; Positie</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Animatie</InputLabel>
                <Select value={karaokeStyle.animationType} label="Animatie" onChange={(e) => setKaraokeStyle((s) => ({ ...s, animationType: e.target.value }))}>
                  <MenuItem value="fade">Fade in</MenuItem>
                  <MenuItem value="scale">Scale in</MenuItem>
                  <MenuItem value="none">Geen</MenuItem>
                </Select>
              </FormControl>
              {karaokeStyle.animationType !== 'none' && (
                <Box sx={{ minWidth: 120, flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">Duur: {karaokeStyle.animDurationMs}ms</Typography>
                  <Slider
                    value={karaokeStyle.animDurationMs}
                    onChange={(_, v) => setKaraokeStyle((s) => ({ ...s, animDurationMs: v }))}
                    min={50} max={500} step={25} size="small"
                  />
                </Box>
              )}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Positie</InputLabel>
                <Select value={karaokeStyle.alignment} label="Positie" onChange={(e) => setKaraokeStyle((s) => ({ ...s, alignment: e.target.value }))}>
                  <MenuItem value={2}>Onder</MenuItem>
                  <MenuItem value={5}>Midden</MenuItem>
                  <MenuItem value={8}>Boven</MenuItem>
                </Select>
              </FormControl>
              <Box sx={{ minWidth: 120, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Marge: {karaokeStyle.marginV}px</Typography>
                <Slider
                  value={karaokeStyle.marginV}
                  onChange={(_, v) => setKaraokeStyle((s) => ({ ...s, marginV: v }))}
                  min={0} max={100} size="small"
                />
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ── Sectie E: Custom woordenboek ── */}
      <Accordion variant="outlined" sx={accordionSx}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBookIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography variant="subtitle2" fontWeight={600}>Custom woordenboek</Typography>
            {vocabularyTerms.length > 0 && (
              <Chip label={`${vocabularyTerms.length} termen`} size="small" color="info" variant="outlined" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Voeg bedrijfsnamen, personen of vakjargon toe om transcriptie te verbeteren.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <TextField
              size="small" label="Term"
              value={newVocabTerm} onChange={(e) => setNewVocabTerm(e.target.value)}
              sx={{ flex: 2, minWidth: 120 }}
            />
            <TextField
              size="small" label="Vervanging (optioneel)"
              value={newVocabReplacement} onChange={(e) => setNewVocabReplacement(e.target.value)}
              sx={{ flex: 2, minWidth: 120 }}
            />
            <FormControl size="small" sx={{ flex: 1, minWidth: 100 }}>
              <InputLabel>Categorie</InputLabel>
              <Select value={newVocabCategory} label="Categorie" onChange={(e) => setNewVocabCategory(e.target.value)}>
                <MenuItem value="">Geen</MenuItem>
                <MenuItem value="bedrijfsnaam">Bedrijfsnaam</MenuItem>
                <MenuItem value="persoon">Persoon</MenuItem>
                <MenuItem value="vakjargon">Vakjargon</MenuItem>
                <MenuItem value="productnaam">Productnaam</MenuItem>
                <MenuItem value="afkorting">Afkorting</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained" size="small"
              onClick={addVocabularyTerm}
              disabled={!newVocabTerm.trim()}
              sx={{ minWidth: 80 }}
            >
              Toevoegen
            </Button>
          </Box>
          {vocabularyTerms.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
              {vocabularyTerms.map((term) => (
                <Chip
                  key={term.id}
                  label={term.replacement ? `${term.term} \u2192 ${term.replacement}` : term.term}
                  size="small"
                  color={term.category === 'bedrijfsnaam' ? 'primary' : term.category === 'persoon' ? 'secondary' : 'default'}
                  onDelete={() => deleteVocabularyTerm(term.id)}
                  title={term.category ? `Categorie: ${term.category}` : ''}
                />
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ── Sectie F: Output ── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Output</Typography>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} color="primary" size="small" />}
              label={<Typography variant="body2">Verstuur per email</Typography>}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Checkbox checked={uploadToFrameIo} onChange={(e) => setUploadToFrameIo(e.target.checked)} color="secondary" size="small" />}
              label={<Typography variant="body2">Upload naar Frame.io</Typography>}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {sendEmail && (
            <Grid item xs={12} sm={uploadToFrameIo ? 6 : 12}>
              <TextField
                fullWidth size="small" type="email" label="Emailadres"
                value={email} onChange={(e) => setEmail(e.target.value)}
                InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: '#999', fontSize: 18 }} /> }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Via Microsoft account ({activeAccount?.username})
              </Typography>
            </Grid>
          )}
          {uploadToFrameIo && (
            <Grid item xs={12} sm={sendEmail ? 6 : 12}>
              <FrameIoFolderPicker
                value={selectedFolderId}
                onChange={(folderId, folderPath) => {
                  setSelectedFolderId(folderId);
                  setSelectedFolderPath(folderPath);
                }}
                hasFrameIoSource={!!frameIoLink}
                showSourceOption={true}
                size="small"
              />
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
}

export default StepSettings;
